import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listPccmHistory, getPccmHistoryVersion, listClasses, listSubjects, listStaff, listTimetableWeeks } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("vi-VN") : "");

// Lịch sử các bản chụp PCCM thật (classSubjects) — tự động chụp mỗi khi Duyệt
// bản nháp (chụp bản TRƯỚC khi ghi đè) hoặc mỗi khi xếp thời khoá biểu (chụp
// đúng PCCM tại thời điểm xếp) — để tra lại 1 TKB cũ đã dùng PCCM nào.
const HappySchoolPccmHistory = () => {
  const schoolId = getActiveSchoolId();
  const [list, setList] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([listPccmHistory(schoolId), listClasses(schoolId), listSubjects(schoolId), listStaff(schoolId), listTimetableWeeks(schoolId)])
      .then(([h, c, s, st, w]) => {
        setList(h);
        setClasses(c);
        setSubjects(s);
        setStaff(st);
        setWeeks(w);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (!schoolId) return <div style={{ padding: 16 }}>Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link>.</div>;

  const classNameById = Object.fromEntries(classes.map((c) => [c.id, c.name]));
  const subjectNameById = Object.fromEntries(subjects.map((s) => [s.id, s.name]));
  const staffNameById = Object.fromEntries(staff.map((t) => [t.id, t.name]));
  const weekLabelById = Object.fromEntries(
    weeks.map((w) => [w.id, `${w.startDate || "?"} – ${w.endDate || "?"}`])
  );

  const toggle = async (v) => {
    if (openId === v.id) { setOpenId(null); setDetail(null); return; }
    setOpenId(v.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await getPccmHistoryVersion(schoolId, v.id);
      setDetail(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const rowsOfSnapshot = (snapshot) => {
    const out = [];
    Object.entries(snapshot || {}).forEach(([classId, subjMap]) => {
      Object.entries(subjMap || {}).forEach(([subjectId, cfg]) => {
        out.push({
          className: classNameById[classId] || classId,
          subjectName: subjectNameById[subjectId] || subjectId,
          teacherName: cfg && cfg.assignedTeacherId ? staffNameById[cfg.assignedTeacherId] || cfg.assignedTeacherId : "—",
          periodsPerWeek: cfg ? cfg.periodsPerWeek : "",
        });
      });
    });
    return out.sort((a, b) => a.className.localeCompare(b.className, "vi", { numeric: true }) || a.subjectName.localeCompare(b.subjectName, "vi"));
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>🕓 Lịch sử PCCM</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Mỗi lần <b>Duyệt bản nháp</b> (chụp PCCM trước khi ghi đè) hoặc <b>xếp thời khoá biểu</b> (chụp đúng PCCM lúc xếp) hệ thống tự lưu 1 bản. Bấm vào 1 dòng để xem chi tiết GV từng lớp/môn tại thời điểm đó.
      </p>
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {loading ? (
        <p>Đang tải...</p>
      ) : list.length === 0 ? (
        <p style={{ color: "#6b7280" }}>Chưa có bản chụp nào.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.map((v) => (
            <div key={v.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
              <button type="button" onClick={() => toggle(v)} style={rowBtn}>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontWeight: 700 }}>{v.reason || "(không rõ lý do)"}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {fmtTime(v.createdAt)}
                    {v.weekId && weekLabelById[v.weekId] && <> · Tuần {weekLabelById[v.weekId]}</>}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: "#0ea5a3" }}>{openId === v.id ? "▲ Ẩn" : "▼ Xem chi tiết"}</span>
              </button>
              {openId === v.id && (
                <div style={{ borderTop: "1px solid #e5e7eb", padding: 12 }}>
                  {detailLoading ? (
                    <p>Đang tải...</p>
                  ) : detail ? (
                    <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
                      <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
                        <thead>
                          <tr>
                            <th style={th}>Lớp</th>
                            <th style={th}>Môn</th>
                            <th style={th}>GV</th>
                            <th style={th}>Tiết/tuần</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowsOfSnapshot(detail.snapshot).map((r, i) => (
                            <tr key={i}>
                              <td style={td}>{r.className}</td>
                              <td style={td}>{r.subjectName}</td>
                              <td style={td}>{r.teacherName}</td>
                              <td style={{ ...td, textAlign: "center" }}>{r.periodsPerWeek}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const rowBtn = { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer" };
const th = { border: "1px solid #e5e7eb", padding: "5px 8px", background: "#f9fafb", textAlign: "left" };
const td = { border: "1px solid #e5e7eb", padding: "4px 8px" };

export default HappySchoolPccmHistory;
